import React, { useState, useMemo } from 'react';
import Button from '@app/react-common/components/button/Button';
import { Checkbox } from '@app/react-common/components/checkbox/Checkbox';
import { StepperInput } from '@app/react-common/components/stepper-input/StepperInput';
import Tooltip from '@app/react-common/components/tooltip/Tooltip';
import { ADJUST_BY_TOOLTIP } from '../utils/consts';
import { SelectedMethodInfo } from '../BulkEditModal';

interface ShippingThresholdsEditViewProps {
  onBack: () => void;
  onClose: () => void;
  onPreviewChanges: (data: ShippingThresholdsFormData) => void;
  currencySign: string;
  selectedMethods: SelectedMethodInfo[];
}

export interface ShippingThresholdsFormData {
  adjustBy: number;
  keepZeroAsMinimum: boolean;
}

export const ShippingThresholdsEditView = ({
  onBack,
  onClose,
  onPreviewChanges,
  currencySign,
  selectedMethods,
}: ShippingThresholdsEditViewProps) => {
  const [adjustBy, setAdjustBy] = useState<number>(0);
  const [isValid, setIsValid] = useState(true);
  const [keepZeroAsMinimum, setKeepZeroAsMinimum] = useState(true);

  // Find the smallest positive threshold value across all selected methods
  // This determines how much we can subtract without going below 0
  const smallestThresholdValue = useMemo(() => {
    const values: number[] = [];

    for (const method of selectedMethods) {
      // Add min values (any positive value)
      const minVal = Number(method.minOrderPriceRange);
      if (method.minOrderPriceRange != null && minVal > 0) {
        values.push(minVal);
      }
      // Add max values (only positive, since 0 means unlimited)
      const maxVal = Number(method.maxOrderPriceRange);
      if (method.maxOrderPriceRange != null && maxVal > 0) {
        values.push(maxVal);
      }
      // Check additional price ranges
      if (method.additionalPriceRanges && Array.isArray(method.additionalPriceRanges)) {
        for (const range of method.additionalPriceRanges) {
          const rangeMinVal = Number(range.minOrderPriceRange);
          if (range.minOrderPriceRange != null && rangeMinVal > 0) {
            values.push(rangeMinVal);
          }
          const rangeMaxVal = Number(range.maxOrderPriceRange);
          if (range.maxOrderPriceRange != null && rangeMaxVal > 0) {
            values.push(rangeMaxVal);
          }
        }
      }
    }

    return values.length > 0 ? Math.min(...values) : null;
  }, [selectedMethods]);

  const wouldGoBelowZero = adjustBy < 0 && smallestThresholdValue !== null && smallestThresholdValue + adjustBy < 0;

  const handlePreviewChanges = () => {
    onPreviewChanges({ adjustBy, keepZeroAsMinimum });
  };

  return (
    <div className="bulk-edit-modal__view">
      <div className="bulk-edit-modal__header">
        <div className="bulk-edit-modal__header__left">
          <Button icon="back" variant="ghost" size="small" onClick={onBack} />
          <h2>Shipping Thresholds</h2>
        </div>
        <Button icon="close" variant="ghost" size="small" onClick={onClose} />
      </div>
      <div className="bulk-edit-modal__content">
        <div className="bulk-edit-modal__field">
          <label className="bulk-edit-modal__label">
            Adjust By
            <Tooltip tooltipText={ADJUST_BY_TOOLTIP} appearOnSide="bottom" maxWidth={200}>
              <i className="icon icon-more_information" />
            </Tooltip>
          </label>
          <div className="bulk-edit-modal__stepper-wrapper">
            <span className="bulk-edit-modal__currency-prefix">{currencySign}</span>
            <StepperInput
              presetValue={adjustBy}
              onChange={setAdjustBy}
              setValid={setIsValid}
              disabled={false}
              min={Number.NEGATIVE_INFINITY}
            />
          </div>
        </div>
        <div className="bulk-edit-modal__field">
          <Checkbox checked={keepZeroAsMinimum} onChange={setKeepZeroAsMinimum} label="Keep 0 as minimum" />
        </div>
      </div>
      <div className="bulk-edit-modal__footer">
        <Button text="Back" onClick={onBack} size="medium" variant="secondary" />
        {!isValid || adjustBy === 0 || wouldGoBelowZero ? (
          <Tooltip
            tooltipText={
              wouldGoBelowZero
                ? `Adjustment would result in negative values. Maximum reduction is ${currencySign}${smallestThresholdValue?.toFixed(2)}`
                : 'Enter an adjustment value to preview changes'
            }
            maxWidth={250}>
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" disabled />
          </Tooltip>
        ) : (
          <div className="bulk-edit-modal__button-wrapper">
            <Button text="Preview Changes" onClick={handlePreviewChanges} size="medium" variant="primary" />
          </div>
        )}
      </div>
    </div>
  );
};
